import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import DateDisplay from './DateDisplay';
import LoadingIndicator from './LoadingIndicator';
import GameCard from './GameCard';
import CelebrationGameCard from './CelebrationGameCard';
import { Game, GamesByDateGroup } from '../types/Games';
import Grid from '@mui/material/Grid';
import { useGameScores } from '@/features/realtime';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Width of the sticky day rail that sits to the left of each date's card grid.
// Floor is set by the longest uppercase weekday ("WEDNESDAY"); everything past
// that is space the cards could be using, so keep the gap tight too.
const DATE_RAIL_WIDTH = 90;

interface GamesContentProps {
  gamesByDate: GamesByDateGroup[];
  dateRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  cardHeight: number;
  allGames: Game[];
  isFetchingPreviousGames: boolean;
  hasMorePreviousGames: boolean;
  isFetchingNextGames: boolean;
  hasMoreNextGames: boolean;
  onLoadPrevious: () => void;
  onLoadNext: () => void;
}

const GamesContent: React.FC<GamesContentProps> = ({
  gamesByDate,
  dateRefs,
  cardHeight,
  allGames,
  isFetchingPreviousGames,
  hasMorePreviousGames,
  isFetchingNextGames,
  hasMoreNextGames,
  onLoadPrevious,
  onLoadNext,
}) => {
  const { gameScores } = useGameScores();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Load Previous Games Button */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 4,
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {allGames.length > 0 && (
          <>
            {isFetchingPreviousGames ? (
              <LoadingIndicator message="Loading previous games..." />
            ) : hasMorePreviousGames ? (
              <Button
                variant="outlined"
                startIcon={<KeyboardArrowUpIcon />}
                onClick={onLoadPrevious}
                sx={{ borderRadius: '50px', px: 3 }}
              >
                Load Earlier Games
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more previous games
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Display games grouped by date */}
      {gamesByDate.map(({ date, formattedDate, games }) => (
        <Box
          key={date}
          ref={(el: HTMLDivElement | null) => {
            dateRefs.current[date] = el;
          }}
          data-date={date}
          sx={{
            display: 'grid',
            gridTemplateColumns: `${DATE_RAIL_WIDTH}px 1fr`,
            gap: 2,
            my: 4,
          }}
        >
          {/* Sticky day rail: weekday, date, game count */}
          <Box sx={{ position: 'sticky', top: 24, alignSelf: 'start', pt: '2px' }}>
            <DateDisplay date={date} gameCount={games.length} />
          </Box>
          <Grid container spacing={3} sx={{ width: '100%' }}>
            {' '}
            {/* Increased spacing between cards and removed left margin */}
            {games.map((game) => {
              let liveScore;
              if (gameScores !== undefined) liveScore = gameScores[game.id];
              return (
                <Grid
                  size={{ xs: 12, md: 6, lg: 4 }}
                  key={game.id || `${game.homeTeam.id}-${game.awayTeam.id}-${game.startTimestamp}`}
                  sx={{ mb: 3 }}
                >
                  {game.isCelebration ? (
                    <CelebrationGameCard game={game} />
                  ) : (
                    <GameCard game={game} cardHeight={cardHeight} liveScore={liveScore} />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}

      {/* Load Next Games Button */}
      <Box
        sx={{
          textAlign: 'center',
          mt: 4,
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {allGames.length > 0 && (
          <>
            {isFetchingNextGames ? (
              <LoadingIndicator message="Loading next games..." />
            ) : hasMoreNextGames ? (
              <Button
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={onLoadNext}
                sx={{ borderRadius: '50px', px: 3 }}
              >
                Load More Games
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more upcoming games
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Handle no games case */}
      {allGames.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h5" color="text.secondary">
            No games found for the selected filters
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default GamesContent;
